import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import { Button, Skeleton, Switch, Table, Tooltip } from '@mantine/core'
import dayjs from 'dayjs'
import utcPlugin from 'dayjs/plugin/utc'
import { ArrowDown, ArrowUp, ChevronDown } from 'lucide-react'
import type { LinkType, StatusLogFiltersInput } from 'types/graphql'

import { navigate } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_PIPELINE_SOURCE_BREAKDOWN,
  GET_PIPELINE_SOURCE_NAME_BREAKDOWN,
} from 'src/graphql/pipelineMetrics.graphql'
import { ApplicantStage } from 'src/graphql/types/applicants'
import { LinkTypeDisplay } from 'src/graphql/types/trackingLinks'
import { canAccessMarketingCosts } from 'src/lib/auth/permissions'
import { encodeLeadSourceValue } from 'src/lib/leadSource.utils'
import { cn } from 'src/utils'

import AnalyticsCard from '../../components/AnalyticsCard'
import { buildApplicantsFilterUrl } from '../buildApplicantsFilterUrl'

import MarketingBudgetDrawer from './MarketingBudgetDrawer'

dayjs.extend(utcPlugin)

const INFO_TEXT =
  'Per-source breakdown for the selected date range. Leads are counted by application date; Hires are counted by hired date — so the same applicant can be in both columns even if they applied before and were hired during the period. Cohort Conv % is the true within-period conversion — of applicants who applied in this period, the fraction eventually hired. Recent windows may trend low while cohorts mature. Time to hire = average days between application date and hired date.'

const COHORT_CONV_TOOLTIP =
  'Cohort conversion: of applicants who applied in this period, the fraction eventually hired. Recent windows may trend low while cohorts are still maturing (applicants need time to be hired).'

const TOP_SOURCES = 4

export interface SourceBreakdownRow {
  source: LinkType
  /** Null on per-type rows; set on the per-link-name child rows. */
  linkName?: string | null
  leads: number
  hires: number
  conversionRate: number | null
  avgDaysToHire: number | null
  cohortHires: number
  cohortConversionRate: number | null
  cohortAgentBookedHires: number
  cohortAgentBookedRate: number | null
  /**
   * The budget entered for this source (or link name, on child rows), summed
   * over the months the window touches — unprorated. Null when the source has
   * no budget line: a dash, never $0, since "no budget entered" and "budgeted
   * zero" are different things.
   */
  totalBudget?: number | null
  /** The share of totalBudget inside the window; what the cost ratios divide. */
  proratedBudget?: number | null
  costPerLead?: number | null
  costPerHire?: number | null
  /**
   * Whether this source has a per-link-name drill-down worth showing. The rows
   * themselves are fetched on demand when the user expands (see
   * GET_PIPELINE_SOURCE_NAME_BREAKDOWN) so the default page load stays cheap.
   */
  hasNameBreakdown?: boolean
}

const TOTAL_BUDGET_TOOLTIP =
  'The budget you entered for this source, added up across every month the selected date range touches. Not adjusted for the range — that is the Pro Rated Budget. Budgets are monthly and company-wide, so they are not sliced by recruiter, job listing, or job type; the cost columns are hidden when those filters are active. A dash means no budget line targets this source.'

const PRORATED_BUDGET_TOOLTIP =
  'The share of the Total Budget that falls inside the selected date range. A whole-month range shows the full budget; a partial one shows less — e.g. Jul 7 – Aug 6 covers 24.8 of July\u2019s 31 days, so a $500 July budget prorates to $400.54. This is the figure Cost per Lead and Cost per Hire divide.'

const COST_PER_LEAD_TOOLTIP =
  'Pro Rated Budget ÷ leads for this source. Uses the same lead count as the Leads column (attributed leads only), and the prorated budget rather than the full one so the numerator and denominator cover the same days.'

const COST_PER_HIRE_TOOLTIP =
  'Pro Rated Budget ÷ cohort hires — the drivers who applied in this window and were eventually hired, i.e. what the spend actually bought. Recent windows read high while cohorts are still maturing.'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

const preciseCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/**
 * Every money cell reads in whole dollars, with the cents on hover.
 *
 * Mixing precisions across columns is what made this table unreadable: a
 * prorated budget of 400.5376 printed as "$401" under Budget and "$400.54"
 * under Cost per Lead, so with a single lead the cost appeared to exceed the
 * budget it was derived from. One rule for every column removes the
 * contradiction; the hover keeps the exact figure a click away.
 */
const formatMoney = (value: number | null | undefined): string => {
  if (value == null) return '—'
  // A real, non-zero cost must not render as "$0" — that reads as free, and
  // "$0" is already meaningful here (a deliberately zero budget).
  if (value !== 0 && Math.abs(value) < 0.5) return '<$1'
  return currencyFormatter.format(value)
}

/** Exact value for the cell's `title`; null cells get no tooltip. */
const preciseMoney = (value: number | null | undefined): string | undefined =>
  value == null ? undefined : preciseCurrencyFormatter.format(value)

/** Whole-dollar money cell; hovering reveals the exact figure. */
const MoneyCell = ({
  value,
  testId,
  emphasis = false,
}: {
  value: number | null | undefined
  testId?: string
  emphasis?: boolean
}) => (
  <Table.Td
    className={cn(
      'text-right font-mono tabular-nums',
      emphasis
        ? cn(
            'font-semibold',
            value == null
              ? 'text-doubleNickel-gray-400'
              : 'text-doubleNickel-gray-900'
          )
        : moneyToneClass(value)
    )}
    data-testid={testId}
    title={preciseMoney(value)}
  >
    {formatMoney(value)}
  </Table.Td>
)

/** Dashes read as absent data, not as a value — same tone as every other dash. */
const moneyToneClass = (value: number | null | undefined): string =>
  value == null ? 'text-doubleNickel-gray-400' : 'text-doubleNickel-gray-700'

/**
 * Budgets are per source and company-wide, so they cannot be attributed to a
 * recruiter, job listing, or job type. With any of those facets active the
 * denominators shrink while spend does not, which would inflate cost per
 * lead/hire — so the columns come out entirely. The resolver nulls the same
 * fields server-side; this just avoids rendering three dash-only columns.
 */
const canShowCostColumns = (
  filters: StatusLogFiltersInput | undefined
): boolean =>
  !filters?.recruiters?.length &&
  !filters?.jobListings?.length &&
  !filters?.jobTypes?.length

/** Label for an expanded child row; unnamed tracking links get a placeholder. */
const childLabel = (linkName: string | null | undefined): string =>
  linkName?.trim() ? linkName : '(No name)'

type SortKey =
  | 'source'
  | 'leads'
  | 'hires'
  | 'avgDaysToHire'
  | 'cohortHires'
  | 'cohortConversionRate'
  | 'totalBudget'
  | 'proratedBudget'
  | 'costPerLead'
  | 'costPerHire'
type SortDirection = 'asc' | 'desc'

const compareNullable = (
  a: number | null | undefined,
  b: number | null | undefined,
  direction: SortDirection
): number => {
  // Sort nulls to the bottom regardless of direction.
  const aNull = a == null
  const bNull = b == null
  if (aNull && bNull) return 0
  if (aNull) return 1
  if (bNull) return -1
  return direction === 'asc' ? a - b : b - a
}

const sortRows = (
  rows: SourceBreakdownRow[],
  sortKey: SortKey,
  direction: SortDirection
): SourceBreakdownRow[] => {
  return [...rows].sort((a, b) => {
    if (sortKey === 'source') {
      const aName = LinkTypeDisplay[a.source] ?? a.source
      const bName = LinkTypeDisplay[b.source] ?? b.source
      return direction === 'asc'
        ? aName.localeCompare(bName)
        : bName.localeCompare(aName)
    }
    if (sortKey === 'avgDaysToHire') {
      return compareNullable(a.avgDaysToHire, b.avgDaysToHire, direction)
    }
    if (sortKey === 'cohortConversionRate') {
      return compareNullable(
        a.cohortConversionRate,
        b.cohortConversionRate,
        direction
      )
    }
    if (
      sortKey === 'totalBudget' ||
      sortKey === 'proratedBudget' ||
      sortKey === 'costPerLead' ||
      sortKey === 'costPerHire'
    ) {
      return compareNullable(a[sortKey], b[sortKey], direction)
    }
    return direction === 'asc'
      ? a[sortKey] - b[sortKey]
      : b[sortKey] - a[sortKey]
  })
}

// `gate` is whichever count's zero-state should produce a dash:
//   - Activity Conv: gate = hires. A 0%-with-zero-hires is dashed because the
//     activity ratio has no meaningful numerator to point at.
//   - Cohort Conv:   gate = leads. A 0%-with-zero-cohort-hires is shown as
//     "0.0%" because the denominator (leads) is the cohort itself — leads > 0
//     with no hires-yet is a real (and often actionable) data point about
//     cohort maturation. See the cohort migration notes for the
//     inconsistency this resolves vs. the Performance tab + XLSX export.
// Color-coding (red below POOR_CONV_THRESHOLD, green above
// GOOD_CONV_THRESHOLD) was removed per user direction — the thresholds
// were calibrated for the OLD activity-conv definition and don't map
// cleanly onto cohort conversion (which trends low while cohorts mature).
// Display the percentage neutrally and let the reader judge.
const formatConvCell = (
  gate: number,
  conversionRate: number | null | undefined
): { text: string; toneClass: string } => {
  if (conversionRate == null || gate === 0) {
    return { text: '—', toneClass: 'text-doubleNickel-gray-400' }
  }
  return {
    text: `${conversionRate.toFixed(1)}%`,
    toneClass: 'text-doubleNickel-gray-700',
  }
}

const formatDays = (avgDaysToHire: number | null | undefined): string => {
  if (avgDaysToHire == null) return '—'
  return `${avgDaysToHire.toFixed(1)}d`
}

interface SortableHeaderProps {
  label: string
  sortKey: SortKey
  activeSortKey: SortKey
  activeDirection: SortDirection
  onSort: (key: SortKey) => void
  align?: 'left' | 'right'
  /**
   * Optional native tooltip surfaced on hover. Used to disambiguate metrics
   * whose definition isn't obvious from the column header (Conv % vs Cohort
   * Conv %, both percentage columns that mean different things).
   */
  tooltip?: string
}

const SortableHeader = ({
  label,
  sortKey,
  activeSortKey,
  activeDirection,
  onSort,
  align = 'right',
  tooltip,
}: SortableHeaderProps) => {
  const isActive = sortKey === activeSortKey
  const Icon = activeDirection === 'asc' ? ArrowUp : ArrowDown
  return (
    <Table.Th
      className={cn(
        'cursor-pointer select-none text-doubleNickel-gray-600 hover:text-doubleNickel-gray-900',
        align === 'right' ? 'text-right' : 'text-left'
      )}
      onClick={() => onSort(sortKey)}
      title={tooltip}
    >
      <span
        className={cn(
          'inline-flex items-center gap-1',
          align === 'right' && 'flex-row-reverse'
        )}
      >
        {label}
        {isActive && <Icon size={12} className="opacity-70" />}
      </span>
    </Table.Th>
  )
}

interface SourceBreakdownCardViewProps {
  rows: SourceBreakdownRow[]
  loading?: boolean
  /**
   * When provided, each Leads/Hires cell and the Source name cell become
   * clickable, navigating to the matching Applicants slice. Left optional so
   * Storybook can render a static, non-navigable preview.
   */
  filters?: StatusLogFiltersInput
  /**
   * Bumped by the page-level Refresh button. Refresh re-pulls the parent rows
   * with the same filters, so it must also invalidate any fetched per-name
   * breakdown — otherwise expanded children would keep showing pre-refresh
   * numbers that no longer reconcile with their parent totals.
   */
  refreshKey?: number
  /**
   * Whether to render the Budget / Cost per Lead / Cost per Hire columns.
   * Driven by the header toggle so the tab can be screen-shared without
   * dollar figures on it. Defaults to shown.
   */
  showCosts?: boolean
}

export const SourceBreakdownCardView = ({
  rows,
  loading = false,
  filters,
  refreshKey = 0,
  showCosts = true,
}: SourceBreakdownCardViewProps) => {
  const [sortKey, setSortKey] = useState<SortKey>('leads')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  // Two independent reasons to hide dollars: the viewer switched them off, or
  // the active filters make company-wide spend unsliceable.
  const showCostColumns = showCosts && canShowCostColumns(filters)

  // Sorting by a column that just disappeared leaves the rows in an order
  // nothing on screen explains — no arrow, no key. Fall back to the default.
  useEffect(() => {
    if (showCostColumns) return
    setSortKey((current) =>
      current === 'totalBudget' ||
      current === 'proratedBudget' ||
      current === 'costPerLead' ||
      current === 'costPerHire'
        ? 'leads'
        : current
    )
  }, [showCostColumns])
  // Which sources are expanded to show their per-link-name breakdown, plus the
  // lazily-fetched rows keyed by source. The breakdown is only queried when a
  // source is first expanded — the table itself never loads it up front.
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set())
  const [childrenBySource, setChildrenBySource] = useState<
    Record<string, SourceBreakdownRow[]>
  >({})
  const [loadingSources, setLoadingSources] = useState<Set<string>>(new Set())

  const [fetchNameBreakdown] = useLazyQuery(
    GET_PIPELINE_SOURCE_NAME_BREAKDOWN,
    {
      // network-only: Refresh re-pulls the parent rows with the SAME variables,
      // so a cache-first lazy query would replay Apollo's pre-refresh result and
      // silently defeat the invalidation below. We only issue this query when we
      // have no rows cached in component state, so it never re-fetches
      // needlessly.
      fetchPolicy: 'network-only',
      onError: () => toast('Failed to load source breakdown', 'error'),
    }
  )

  // Value-based identity for the active filter window. The parent rebuilds
  // `filters` inline on every render, so keying the invalidation effect off the
  // object itself would collapse open drill-downs on unrelated re-renders (a
  // hero query completing, Refresh bumping its key, …).
  const filtersKey = useMemo(() => JSON.stringify(filters ?? null), [filters])

  // Bumped whenever the window changes, so responses from a superseded window
  // can be recognized and dropped instead of repopulating the cache.
  const requestGeneration = useRef(0)

  const loadChildren = useCallback(
    async (source: string) => {
      const generation = requestGeneration.current
      setLoadingSources((prev) => new Set(prev).add(source))
      try {
        const { data } = await fetchNameBreakdown({
          variables: { source, filters },
        })
        // Filters moved on while this was in flight — the cache has already
        // been cleared for the new window, so this payload is stale.
        if (generation !== requestGeneration.current) return
        const rows = data?.pipelineSourceNameBreakdown
        // On failure Apollo resolves with no data (onError has toasted). Leave
        // the cache entry unset so collapsing and re-expanding retries rather
        // than showing a permanently empty drill-down.
        if (!rows) return
        setChildrenBySource((prev) => ({ ...prev, [source]: rows }))
      } finally {
        // If the generation advanced, the invalidation effect already cleared
        // every loading flag — don't resurrect state for the old window.
        if (generation === requestGeneration.current) {
          setLoadingSources((prev) => {
            const next = new Set(prev)
            next.delete(source)
            return next
          })
        }
      }
    },
    [fetchNameBreakdown, filters]
  )

  // A new filter window — or an explicit Refresh, which re-pulls the parent
  // rows with the same filters — invalidates every fetched breakdown: drop the
  // cache, collapse rows, clear loading flags, and invalidate in-flight
  // requests so a late response can't reintroduce superseded numbers. Rows
  // collapse rather than silently refetching, matching the filter-change path
  // and avoiding a burst of one request per open source.
  useEffect(() => {
    requestGeneration.current += 1
    setChildrenBySource({})
    setExpandedSources(new Set())
    setLoadingSources(new Set())
  }, [filtersKey, refreshKey])

  const toggleSource = (source: string) => {
    const isOpening = !expandedSources.has(source)
    setExpandedSources((prev) => {
      const next = new Set(prev)
      if (next.has(source)) {
        next.delete(source)
      } else {
        next.add(source)
      }
      return next
    })
    // Fetch on first expand only; cached thereafter until filters change. A
    // previously failed source has no cache entry, so it retries here.
    if (isOpening && !childrenBySource[source] && !loadingSources.has(source)) {
      void loadChildren(source)
    }
  }

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDirection(key === 'source' ? 'asc' : 'desc')
    }
  }

  const sortedRows = useMemo(
    () => sortRows(rows, sortKey, sortDirection),
    [rows, sortKey, sortDirection]
  )

  // Only reserve the chevron gutter when at least one row can expand —
  // otherwise every source label would be indented for an affordance that
  // isn't there, changing the table's look for no reason.
  const anyExpandable = useMemo(
    () => rows.some((r) => r.hasNameBreakdown),
    [rows]
  )

  // Bar widths are computed relative to the largest leads count across all
  // rows (not the current sort) so the visual ranking is stable as the user
  // sorts by other columns.
  const maxLeads = useMemo(
    () => rows.reduce((max, r) => (r.leads > max ? r.leads : max), 0),
    [rows]
  )

  // Identify the top N sources by leads to color their bars with the brand
  // color; the rest get a muted gray. This is independent of the current sort.
  const topSourceSet = useMemo(() => {
    const sortedByLeads = [...rows].sort((a, b) => b.leads - a.leads)
    return new Set(
      sortedByLeads.slice(0, TOP_SOURCES).map((r) => r.source as string)
    )
  }, [rows])

  // Overall totals — used for the bottom Total row.
  const totals = useMemo(() => {
    const totalLeads = rows.reduce((s, r) => s + r.leads, 0)
    const totalHires = rows.reduce((s, r) => s + r.hires, 0)
    const totalCohortHires = rows.reduce((s, r) => s + r.cohortHires, 0)
    // Conversion is hires-over-leads at the company level — NOT an average of
    // per-source rates (that would double-weight low-volume sources).
    // Cohort conv at the company level uses the same denominator (totalLeads)
    // but the cohort-pure numerator. Stays in lockstep with the per-source
    // cohort rate definition.
    const overallCohortConv =
      totalLeads > 0 ? (totalCohortHires / totalLeads) * 100 : null
    // Time-to-hire weighted by each source's hire count, so big-volume
    // sources dominate the overall mean. Sources with zero hires don't
    // contribute to either numerator or denominator.
    const weightedDaysSum = rows.reduce(
      (s, r) =>
        r.avgDaysToHire != null && r.hires > 0
          ? s + r.avgDaysToHire * r.hires
          : s,
      0
    )
    const overallDays = totalHires > 0 ? weightedDaysSum / totalHires : null
    // Budget totals only exist once some source carries one; null (dash) when
    // none do, so an unbudgeted period never reads as $0 spent. Overall cost
    // is total-over-total, matching every other rate in this row.
    //
    // This is the sum of the ATTRIBUTED source budgets — deliberately not the
    // window's whole spend: a budgeted source with no leads and no hires in the
    // window has no row here to carry its money, so the drawer's month total
    // can legitimately exceed this. The column header tooltip says so.
    const sumOf = (
      pick: (r: SourceBreakdownRow) => number | null | undefined
    ) => {
      const present = rows.filter((r) => pick(r) != null)
      return present.length > 0
        ? present.reduce((sum, r) => sum + (pick(r) ?? 0), 0)
        : null
    }
    const totalBudgetSum = sumOf((r) => r.totalBudget)
    const proratedBudgetSum = sumOf((r) => r.proratedBudget)
    // Ratios divide the PRORATED sum so numerator and denominator cover the
    // same days as the per-row figures above them.
    const overallCostPerLead =
      proratedBudgetSum != null && totalLeads > 0
        ? proratedBudgetSum / totalLeads
        : null
    const overallCostPerHire =
      proratedBudgetSum != null && totalCohortHires > 0
        ? proratedBudgetSum / totalCohortHires
        : null
    return {
      totalBudgetSum,
      proratedBudgetSum,
      totalLeads,
      totalHires,
      totalCohortHires,
      overallCohortConv,
      overallDays,
      overallCostPerLead,
      overallCostPerHire,
    }
  }, [rows])

  // Per-row, per-metric click handlers. Leads cells filter on application
  // date + that source; Hires cells filter on hired date + that source. Both
  // are stage-event semantics — matches the resolver, which counts Leads by
  // applicationDate and Hires by hiredDate independently. Conv % and Time to
  // Hire are rates/averages and stay non-clickable: there's no single cohort
  // to land on.
  // `linkName` is passed for the expanded per-name child rows: the Applicants
  // lead-source filter understands the same "TYPE:name" encoding this builds,
  // so a child row lands on exactly that tracking link's applicants.
  const goToLeadsForSource = (source: LinkType, linkName?: string | null) => {
    if (!filters?.dateRange) return
    navigate(
      buildApplicantsFilterUrl({
        dateRange: filters.dateRange,
        recruiterIds: filters.recruiters ?? undefined,
        jobListingIds: filters.jobListings ?? undefined,
        leadSources: [encodeLeadSourceValue(source, linkName)],
      })
    )
  }
  const goToHiresForSource = (source: LinkType, linkName?: string | null) => {
    if (!filters?.dateRange) return
    navigate(
      buildApplicantsFilterUrl({
        stage: ApplicantStage.HIRED,
        dateRange: filters.dateRange,
        recruiterIds: filters.recruiters ?? undefined,
        jobListingIds: filters.jobListings ?? undefined,
        leadSources: [encodeLeadSourceValue(source, linkName)],
      })
    )
  }
  // Total row: no source filter — the FiltersBar's linkTypes selection still
  // applies, so the page-level filter pass-through preserves the cohort the
  // user is currently viewing on Analytics.
  const goToLeadsForAllSources = () => {
    if (!filters?.dateRange) return
    navigate(
      buildApplicantsFilterUrl({
        dateRange: filters.dateRange,
        recruiterIds: filters.recruiters ?? undefined,
        jobListingIds: filters.jobListings ?? undefined,
        leadSources: (filters.linkTypes as string[]) ?? undefined,
      })
    )
  }
  const goToHiresForAllSources = () => {
    if (!filters?.dateRange) return
    navigate(
      buildApplicantsFilterUrl({
        stage: ApplicantStage.HIRED,
        dateRange: filters.dateRange,
        recruiterIds: filters.recruiters ?? undefined,
        jobListingIds: filters.jobListings ?? undefined,
        leadSources: (filters.linkTypes as string[]) ?? undefined,
      })
    )
  }

  const clickable = Boolean(filters?.dateRange)
  const clickableCellClass = clickable ? 'cursor-pointer hover:underline' : ''

  if (loading) {
    // One placeholder per column the loaded table will actually draw, so
    // nothing shifts sideways on resolve. Kept in step with the header and the
    // empty-state colSpan below (7 columns, or 11 with the cost columns on).
    const skeletonWidths: (number | string)[] = [
      '60%',
      '100%',
      36,
      36,
      36,
      48,
      48,
      ...(showCostColumns ? [56, 56, 56, 56] : []),
    ]
    return (
      <Table verticalSpacing="xs" horizontalSpacing="sm" fz="xs">
        <Table.Tbody>
          {Array.from({ length: 5 }).map((_, i) => (
            <Table.Tr key={i}>
              {skeletonWidths.map((width, cell) => (
                <Table.Td key={cell}>
                  <Skeleton height={cell === 1 ? 14 : 12} width={width} />
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-xs text-doubleNickel-gray-500">
        No data for this period.
      </div>
    )
  }

  const totalDaysFormatted = formatDays(totals.overallDays)

  return (
    <Table verticalSpacing="xs" horizontalSpacing="sm" fz="xs">
      <Table.Thead>
        <Table.Tr>
          <SortableHeader
            label="Source"
            sortKey="source"
            activeSortKey={sortKey}
            activeDirection={sortDirection}
            onSort={handleSort}
            align="left"
          />
          <Table.Th
            className="text-doubleNickel-gray-600"
            style={{ width: 160 }}
          >
            Volume
          </Table.Th>
          <SortableHeader
            label="Leads"
            sortKey="leads"
            activeSortKey={sortKey}
            activeDirection={sortDirection}
            onSort={handleSort}
          />
          <SortableHeader
            label="Hires"
            sortKey="hires"
            activeSortKey={sortKey}
            activeDirection={sortDirection}
            onSort={handleSort}
          />
          <SortableHeader
            label="Cohort Hires"
            sortKey="cohortHires"
            activeSortKey={sortKey}
            activeDirection={sortDirection}
            onSort={handleSort}
            tooltip="Of applicants who applied in this period, how many were eventually hired. Strict subset of Leads."
          />
          <SortableHeader
            label="Cohort Conv %"
            sortKey="cohortConversionRate"
            activeSortKey={sortKey}
            activeDirection={sortDirection}
            onSort={handleSort}
            tooltip={COHORT_CONV_TOOLTIP}
          />
          <SortableHeader
            label="Time to Hire"
            sortKey="avgDaysToHire"
            activeSortKey={sortKey}
            activeDirection={sortDirection}
            onSort={handleSort}
          />
          {showCostColumns && (
            <>
              <SortableHeader
                label="Total Budget"
                sortKey="totalBudget"
                activeSortKey={sortKey}
                activeDirection={sortDirection}
                onSort={handleSort}
                tooltip={TOTAL_BUDGET_TOOLTIP}
              />
              <SortableHeader
                label="Pro Rated Budget"
                sortKey="proratedBudget"
                activeSortKey={sortKey}
                activeDirection={sortDirection}
                onSort={handleSort}
                tooltip={PRORATED_BUDGET_TOOLTIP}
              />
              <SortableHeader
                label="Cost per Lead"
                sortKey="costPerLead"
                activeSortKey={sortKey}
                activeDirection={sortDirection}
                onSort={handleSort}
                tooltip={COST_PER_LEAD_TOOLTIP}
              />
              <SortableHeader
                label="Cost per Hire"
                sortKey="costPerHire"
                activeSortKey={sortKey}
                activeDirection={sortDirection}
                onSort={handleSort}
                tooltip={COST_PER_HIRE_TOOLTIP}
              />
            </>
          )}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {sortedRows.map((row) => {
          const days = formatDays(row.avgDaysToHire)
          const widthPct =
            maxLeads > 0 ? Math.round((row.leads / maxLeads) * 100) : 0
          const isTopSource = topSourceSet.has(row.source as string)
          // The backend flags which sources have more than one link-name
          // bucket, so we can offer an expander without having fetched the
          // breakdown. Rows arrive later, on demand.
          const hasChildren = Boolean(row.hasNameBreakdown)
          const isExpanded = expandedSources.has(row.source as string)
          const children = childrenBySource[row.source as string] ?? []
          const isLoadingChildren = loadingSources.has(row.source as string)
          return (
            <React.Fragment key={row.source}>
              <Table.Tr>
                <Table.Td
                  className={cn(
                    'font-medium text-doubleNickel-gray-800',
                    (hasChildren || clickable) && clickableCellClass
                  )}
                  onClick={
                    hasChildren
                      ? () => toggleSource(row.source as string)
                      : clickable
                      ? () => goToLeadsForSource(row.source)
                      : undefined
                  }
                  data-testid={
                    hasChildren ? `source-toggle-${row.source}` : undefined
                  }
                >
                  <span className="inline-flex items-center gap-2">
                    {/* Gutter only exists when something in the table can
                        expand, so labels stay put on tables with no
                        breakdowns. */}
                    {anyExpandable && (
                      <span
                        aria-hidden="true"
                        className="inline-flex h-[14px] w-[14px] flex-shrink-0 items-center justify-center text-doubleNickel-gray-400"
                      >
                        {hasChildren && (
                          <ChevronDown
                            size={10}
                            strokeWidth={2}
                            className={cn(
                              'transition-transform',
                              !isExpanded && '-rotate-90'
                            )}
                          />
                        )}
                      </span>
                    )}
                    <span>{LinkTypeDisplay[row.source] ?? row.source}</span>
                  </span>
                </Table.Td>
                <Table.Td>
                  <div
                    className="h-2 rounded-full bg-doubleNickel-gray-100"
                    data-testid={`bar-track-${row.source}`}
                  >
                    <div
                      className={cn(
                        'h-full rounded-full transition-all',
                        isTopSource
                          ? 'bg-doubleNickel-brand-500'
                          : 'bg-doubleNickel-gray-400'
                      )}
                      style={{ width: `${widthPct}%` }}
                      data-testid={`bar-fill-${row.source}`}
                      data-width-pct={widthPct}
                      data-top-source={isTopSource ? 'true' : 'false'}
                    />
                  </div>
                </Table.Td>
                <Table.Td
                  className={cn(
                    'text-right font-mono tabular-nums text-doubleNickel-gray-800',
                    clickableCellClass
                  )}
                  onClick={
                    clickable ? () => goToLeadsForSource(row.source) : undefined
                  }
                >
                  {row.leads.toLocaleString()}
                </Table.Td>
                <Table.Td
                  className={cn(
                    'text-right font-mono font-semibold tabular-nums text-doubleNickel-gray-800',
                    clickableCellClass
                  )}
                  onClick={
                    clickable ? () => goToHiresForSource(row.source) : undefined
                  }
                >
                  {row.hires.toLocaleString()}
                </Table.Td>
                {/* Cohort Hires + Cohort Conv % — non-clickable for now. The
                  cohort drill-down would need to filter HIRED applicants by
                  applicationDate (not hiredDate), which the current URL
                  builder doesn't support natively; treating it as a follow-up
                  rather than wiring a half-correct click target. */}
                <Table.Td
                  className="text-right font-mono font-semibold tabular-nums text-doubleNickel-gray-800"
                  data-testid={`cohort-hires-${row.source}`}
                >
                  {row.cohortHires.toLocaleString()}
                </Table.Td>
                <Table.Td
                  className={cn(
                    'text-right font-mono tabular-nums',
                    formatConvCell(row.leads, row.cohortConversionRate)
                      .toneClass
                  )}
                  data-testid={`cohort-conv-${row.source}`}
                >
                  {formatConvCell(row.leads, row.cohortConversionRate).text}
                </Table.Td>
                <Table.Td
                  className={cn(
                    'text-right font-mono tabular-nums',
                    days === '—'
                      ? 'text-doubleNickel-gray-400'
                      : 'text-doubleNickel-gray-700'
                  )}
                >
                  {days}
                </Table.Td>
                {showCostColumns && (
                  <>
                    <MoneyCell
                      value={row.totalBudget}
                      testId={`total-budget-${row.source}`}
                    />
                    <MoneyCell
                      value={row.proratedBudget}
                      testId={`prorated-budget-${row.source}`}
                    />
                    <MoneyCell
                      value={row.costPerLead}
                      testId={`cost-per-lead-${row.source}`}
                    />
                    <MoneyCell
                      value={row.costPerHire}
                      testId={`cost-per-hire-${row.source}`}
                    />
                  </>
                )}
              </Table.Tr>
              {/* Fetching the breakdown for a just-expanded source. */}
              {isExpanded && isLoadingChildren && (
                <Table.Tr
                  className="bg-doubleNickel-gray-25"
                  data-testid={`source-child-loading-${row.source}`}
                >
                  <Table.Td
                    colSpan={showCostColumns ? 11 : 7}
                    className="py-2 pl-[22px]"
                  >
                    <Skeleton height={10} width="45%" />
                  </Table.Td>
                </Table.Tr>
              )}
              {/* Per-link-name breakdown. Same columns as the parent so numbers
                read down the column; counts sum back to the parent row. */}
              {isExpanded &&
                !isLoadingChildren &&
                children.map((child) => {
                  const childDays = formatDays(child.avgDaysToHire)
                  const childWidthPct =
                    maxLeads > 0
                      ? Math.round((child.leads / maxLeads) * 100)
                      : 0
                  const childKey = `${row.source}:${child.linkName ?? ''}`
                  return (
                    <Table.Tr
                      key={childKey}
                      className="bg-doubleNickel-gray-25"
                      data-testid={`source-child-${childKey}`}
                    >
                      <Table.Td className="text-doubleNickel-gray-700">
                        {/* Indent aligns child names under the parent label. */}
                        <span className="inline-flex items-center gap-2 pl-[22px]">
                          {childLabel(child.linkName)}
                        </span>
                      </Table.Td>
                      <Table.Td>
                        <div className="h-2 rounded-full bg-doubleNickel-gray-100">
                          <div
                            className="h-full rounded-full bg-doubleNickel-gray-300 transition-all"
                            style={{ width: `${childWidthPct}%` }}
                            data-testid={`child-bar-fill-${childKey}`}
                            data-width-pct={childWidthPct}
                          />
                        </div>
                      </Table.Td>
                      <Table.Td
                        className={cn(
                          'text-right font-mono tabular-nums text-doubleNickel-gray-700',
                          clickableCellClass
                        )}
                        onClick={
                          clickable
                            ? () =>
                                goToLeadsForSource(row.source, child.linkName)
                            : undefined
                        }
                      >
                        {child.leads.toLocaleString()}
                      </Table.Td>
                      <Table.Td
                        className={cn(
                          'text-right font-mono tabular-nums text-doubleNickel-gray-700',
                          clickableCellClass
                        )}
                        onClick={
                          clickable
                            ? () =>
                                goToHiresForSource(row.source, child.linkName)
                            : undefined
                        }
                      >
                        {child.hires.toLocaleString()}
                      </Table.Td>
                      <Table.Td className="text-right font-mono tabular-nums text-doubleNickel-gray-700">
                        {child.cohortHires.toLocaleString()}
                      </Table.Td>
                      <Table.Td
                        className={cn(
                          'text-right font-mono tabular-nums',
                          formatConvCell(
                            child.leads,
                            child.cohortConversionRate
                          ).toneClass
                        )}
                      >
                        {
                          formatConvCell(
                            child.leads,
                            child.cohortConversionRate
                          ).text
                        }
                      </Table.Td>
                      <Table.Td
                        className={cn(
                          'text-right font-mono tabular-nums',
                          childDays === '—'
                            ? 'text-doubleNickel-gray-400'
                            : 'text-doubleNickel-gray-700'
                        )}
                      >
                        {childDays}
                      </Table.Td>
                      {showCostColumns && (
                        <>
                          {/* Child budgets come from per-link-name budget
                            lines. The "(No name)" bucket never matches one —
                            a budget line with no link name means the whole
                            source — so it always dashes. */}
                          <MoneyCell
                            value={child.totalBudget}
                            testId={`child-total-budget-${childKey}`}
                          />
                          <MoneyCell
                            value={child.proratedBudget}
                            testId={`child-prorated-budget-${childKey}`}
                          />
                          <MoneyCell value={child.costPerLead} />
                          <MoneyCell value={child.costPerHire} />
                        </>
                      )}
                    </Table.Tr>
                  )
                })}
            </React.Fragment>
          )
        })}

        {/* Total row — overall pipeline summary across all sources. */}
        <Table.Tr
          className="border-t-2 border-doubleNickel-gray-200 bg-doubleNickel-gray-25"
          data-testid="total-row"
        >
          <Table.Td className="font-semibold text-doubleNickel-gray-900">
            Total
          </Table.Td>
          <Table.Td />
          <Table.Td
            className={cn(
              'text-right font-mono font-semibold tabular-nums text-doubleNickel-gray-900',
              clickableCellClass
            )}
            onClick={clickable ? goToLeadsForAllSources : undefined}
          >
            {totals.totalLeads.toLocaleString()}
          </Table.Td>
          <Table.Td
            className={cn(
              'text-right font-mono font-semibold tabular-nums text-doubleNickel-gray-900',
              clickableCellClass
            )}
            onClick={clickable ? goToHiresForAllSources : undefined}
          >
            {totals.totalHires.toLocaleString()}
          </Table.Td>
          <Table.Td
            className="text-right font-mono font-semibold tabular-nums text-doubleNickel-gray-900"
            data-testid="total-cohort-hires"
          >
            {totals.totalCohortHires.toLocaleString()}
          </Table.Td>
          <Table.Td
            className={cn(
              'text-right font-mono font-semibold tabular-nums',
              formatConvCell(totals.totalLeads, totals.overallCohortConv)
                .toneClass
            )}
            data-testid="total-cohort-conv"
          >
            {formatConvCell(totals.totalLeads, totals.overallCohortConv).text}
          </Table.Td>
          <Table.Td
            className={cn(
              'text-right font-mono font-semibold tabular-nums',
              totalDaysFormatted === '—'
                ? 'text-doubleNickel-gray-400'
                : 'text-doubleNickel-gray-900'
            )}
          >
            {totalDaysFormatted}
          </Table.Td>
          {showCostColumns && (
            <>
              <MoneyCell
                value={totals.totalBudgetSum}
                testId="total-total-budget"
                emphasis
              />
              <MoneyCell
                value={totals.proratedBudgetSum}
                testId="total-prorated-budget"
                emphasis
              />
              <MoneyCell
                value={totals.overallCostPerLead}
                testId="total-cost-per-lead"
                emphasis
              />
              <MoneyCell
                value={totals.overallCostPerHire}
                testId="total-cost-per-hire"
                emphasis
              />
            </>
          )}
        </Table.Tr>
      </Table.Tbody>
    </Table>
  )
}

export interface SourceBreakdownCardProps {
  filters: StatusLogFiltersInput
  /**
   * Bumped by the page-level Refresh button — when this value changes (after
   * the initial mount), the card calls its own `refetch()` to re-pull data
   * with the same filters.
   */
  refreshKey: number
  /**
   * Called after a budget is saved, so the tab can invalidate this card —
   * including any drill-down rows it holds in local state.
   */
  onBudgetSaved?: () => void
}

const SourceBreakdownCard = ({
  filters,
  refreshKey,
  onBudgetSaved,
}: SourceBreakdownCardProps) => {
  // The budget behind the cost columns is edited from this card's header, so
  // the drawer lives here rather than in a separate spend card.
  const [drawerMonth, setDrawerMonth] = useState<string | null>(null)

  // Two separate ideas, deliberately not collapsed into one:
  //   canSeeCosts — may this user see spend at all (permission, server-enforced)
  //   showCosts   — has this user chosen to reveal it right now (screen-share)
  const { currentUser } = useAuth()
  const canSeeCosts = canAccessMarketingCosts(currentUser)

  // Dollars start hidden on every visit and are opened on request: the tab gets
  // screen-shared often, so revealing spend is a deliberate act. Deliberately
  // not persisted — a remembered "on" would put the money back on screen
  // without anyone asking for it.
  const [showCosts, setShowCosts] = useState(false)

  const openBudgetDrawer = useCallback(() => {
    // Open on the month the window mostly covers — deliberately the MIDPOINT,
    // not either edge. The date pickers emit local-midnight bounds, so a
    // whole-month pick straddles two UTC months in opposite directions
    // depending on the viewer's offset: New York sends
    // (Jul 1 04:00Z … Aug 1 03:59Z) and London sends
    // (Jun 30 23:00Z … Jul 31 22:59Z). Truncating `lte` opens August for the
    // first; truncating `gte` opens June for the second. Either way the drawer
    // labels one month while the Budget column bills another, and saving there
    // replaces a different month's lines (the upsert rewrites a whole month).
    // The midpoint lands inside the intended month for every offset.
    const fallback = new Date().toISOString()
    const start = filters?.dateRange?.gte ?? filters?.dateRange?.lte ?? fallback
    const end = filters?.dateRange?.lte ?? start
    const startMs = dayjs.utc(start).valueOf()
    const endMs = dayjs.utc(end).valueOf()
    const midpoint = startMs + Math.round((endMs - startMs) / 2)
    setDrawerMonth(dayjs.utc(midpoint).startOf('month').toISOString())
  }, [filters])

  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const { data, loading, refetch } = useQuery(GET_PIPELINE_SOURCE_BREAKDOWN, {
    variables: { filters },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  // Skip the initial mount — the useQuery above already kicked off the first
  // fetch. Only call refetch on subsequent refreshKey changes.
  const isFirstRefreshKey = useRef(true)
  useEffect(() => {
    if (isFirstRefreshKey.current) {
      isFirstRefreshKey.current = false
      return
    }
    refetch()
  }, [refreshKey, refetch])

  const rows = (data?.pipelineSourceBreakdown ?? []) as SourceBreakdownRow[]

  return (
    <>
      <AnalyticsCard
        title="Pipeline by Source"
        info={INFO_TEXT}
        headerChildren={
          // Both controls are about money, so neither exists for a user
          // without the grant — no disabled affordance hinting at data they
          // can't have. The server withholds the figures regardless.
          canSeeCosts ? (
            <div className="flex items-center gap-3">
              <Tooltip
                multiline
                w={280}
                withArrow
                position="bottom"
                label={
                  <div className="flex flex-col gap-1 py-0.5">
                    <span>Adds three columns to the table:</span>
                    <span>
                      <b>Budget</b> — what you budgeted for that source,
                      prorated to the selected dates.
                    </span>
                    <span>
                      <b>Cost per Lead</b> — budget ÷ that source&#39;s leads.
                    </span>
                    <span>
                      <b>Cost per Hire</b> — budget ÷ the drivers from those
                      leads who were hired.
                    </span>
                  </div>
                }
              >
                {/* Wrapper span: Tooltip needs a ref-forwarding child, and it
                  has to cover the label too, which sits outside the input. */}
                <span>
                  <Switch
                    size="xs"
                    color="#1C93D7"
                    label="Costs"
                    labelPosition="left"
                    checked={showCosts}
                    onChange={(event) =>
                      setShowCosts(event.currentTarget.checked)
                    }
                    classNames={{ label: 'text-xs text-doubleNickel-gray-600' }}
                    aria-label="Show cost columns"
                    data-testid="show-costs-toggle"
                  />
                </span>
              </Tooltip>
              <Button
                size="compact-xs"
                variant="light"
                onClick={openBudgetDrawer}
                data-testid="manage-budgets-button"
              >
                Manage budgets
              </Button>
            </div>
          ) : null
        }
      >
        <SourceBreakdownCardView
          rows={rows}
          loading={loading}
          filters={filters}
          refreshKey={refreshKey}
          showCosts={canSeeCosts && showCosts}
        />
      </AnalyticsCard>

      <MarketingBudgetDrawer
        opened={canSeeCosts && drawerMonth != null}
        month={drawerMonth}
        onClose={() => setDrawerMonth(null)}
        onSaved={() => {
          refetch()
          onBudgetSaved?.()
        }}
      />
    </>
  )
}

export default SourceBreakdownCard
